import React from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Layers, ChevronDown, Lock, Calendar, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

const CalendarLayers = ({
  // Published Events filter
  publishedFilter, // 'all' | 'mine'
  onPublishedFilterChange,
  // Holds toggles
  showMyHolds,
  onShowMyHoldsChange,
  showAllHolds,
  onShowAllHoldsChange,
  // Private Events overlay
  showPrivateEvents,
  onShowPrivateEventsChange,
  // Counts for badges
  myHoldsCount = 0,
  allHoldsCount = 0,
  privateEventsCount = 0,
  // Mint style for standardized color
  mintStyle = false,
}) => {
  const activeLayersCount = [
    showMyHolds,
    showAllHolds,
    showPrivateEvents,
  ].filter(Boolean).length;

  const LayersContent = () => (
    <div className="space-y-6">
      {/* Published Events Filter */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Calendar className="h-4 w-4" />
          Published Events
        </div>
        <div className="flex rounded-lg border p-1 bg-muted/30">
          <button
            onClick={() => onPublishedFilterChange('all')}
            className={cn(
              "flex-1 px-3 py-1.5 text-sm rounded-md transition-colors",
              publishedFilter === 'all'
                ? "bg-background shadow-sm font-medium"
                : "text-muted-foreground hover:text-foreground"
            )}
            data-testid="filter-all-published"
          >
            All Published
          </button>
          <button
            onClick={() => onPublishedFilterChange('mine')}
            className={cn(
              "flex-1 px-3 py-1.5 text-sm rounded-md transition-colors",
              publishedFilter === 'mine'
                ? "bg-background shadow-sm font-medium"
                : "text-muted-foreground hover:text-foreground"
            )}
            data-testid="filter-mine-only"
          >
            Mine Only
          </button>
        </div>
      </div>

      {/* Holds Overlays */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Users className="h-4 w-4" />
          Holds Overlays
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="my-holds" className="text-sm cursor-pointer">
                My Holds
              </Label>
              {myHoldsCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-full">
                  {myHoldsCount}
                </span>
              )}
            </div>
            <Switch
              id="my-holds"
              checked={showMyHolds}
              onCheckedChange={onShowMyHoldsChange}
              data-testid="toggle-my-holds"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="all-holds" className="text-sm cursor-pointer">
                All Holds
              </Label>
              <span className="text-xs text-muted-foreground">(anonymous)</span>
              {allHoldsCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded-full">
                  {allHoldsCount}
                </span>
              )}
            </div>
            <Switch
              id="all-holds"
              checked={showAllHolds}
              onCheckedChange={onShowAllHoldsChange}
              data-testid="toggle-all-holds"
            />
          </div>
        </div>
      </div>

      {/* Private Events Overlay */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Lock className="h-4 w-4" />
          Private Events
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label htmlFor="private-events" className="text-sm cursor-pointer">
              My Private Events
            </Label>
            {privateEventsCount > 0 && (
              <span className="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 rounded-full">
                {privateEventsCount}
              </span>
            )}
          </div>
          <Switch
            id="private-events"
            checked={showPrivateEvents}
            onCheckedChange={onShowPrivateEventsChange}
            data-testid="toggle-private-events"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Shows your private events as an overlay. They remain workspace-only.
        </p>
      </div>
    </div>
  );

  // Desktop: Popover
  const DesktopLayers = () => (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "gap-2",
              mintStyle && "bg-[#7FD1C6] hover:bg-[#6BC4B8] text-black border-[#7FD1C6]"
            )}
            data-testid="layers-btn"
          >
            <Layers className="h-4 w-4" />
            Customize Calendar View
            {activeLayersCount > 0 && (
              <span className={cn(
                "ml-1 px-1.5 py-0.5 text-xs rounded-full",
                mintStyle ? "bg-black/10 text-black" : "bg-primary text-primary-foreground"
              )}>
                {activeLayersCount}
              </span>
            )}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="end">
          <LayersContent />
        </PopoverContent>
      </Popover>
    </div>
  );

  // Mobile: Bottom Sheet
  const MobileLayers = () => (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "gap-2",
            mintStyle && "bg-[#7FD1C6] hover:bg-[#6BC4B8] text-black border-[#7FD1C6]"
          )}
          data-testid="layers-btn-mobile"
        >
          <Layers className="h-4 w-4" />
          Customize View
          {activeLayersCount > 0 && (
            <span className={cn(
              "px-1.5 py-0.5 text-xs rounded-full",
              mintStyle ? "bg-black/10 text-black" : "bg-primary text-primary-foreground"
            )}>
              {activeLayersCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-auto max-h-[80vh]">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Customize Calendar View
          </SheetTitle>
        </SheetHeader>
        <LayersContent />
      </SheetContent>
    </Sheet>
  );

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:block">
        <DesktopLayers />
      </div>
      {/* Mobile */}
      <div className="md:hidden">
        <MobileLayers />
      </div>
    </>
  );
};

export default CalendarLayers;
